import React, { useRef, useState } from "react";
import { STAGES, VISIBLE_STAGES } from "../constants/config";
import KanbanColumn from "./KanbanColumn";
import KanbanStageModal, {
  VALID_TRANSITIONS,
  DISABLED_DRAG_FROM,
} from "./KanbanStageModal";

const BoardView = ({
  cards,
  dragOver,
  setDragOver,
  dragId,
  setDragId,
  setCards,
  newCardIds = new Set(),
  removingCardIds = new Set(),
  movingCardIds = new Set(),
  onRefresh,
  isFetching = false,
  onCardMoved,
}) => {
  const scrollRef = useRef();

  // ── modal state ──────────────────────────────────────────────────
  const [modalOpen, setModalOpen] = useState(false);
  const [modalCard, setModalCard] = useState(null);
  const [modalFromStage, setModalFromStage] = useState(null);
  const [modalToStage, setModalToStage] = useState(null);
  // ── add event modal state ──────────────────────────────────────
  const [addEventOpen, setAddEventOpen] = useState(false);
  const [addEventCard, setAddEventCard] = useState(null);

  const handleAddEvent = (card) => {
    setAddEventCard(card);
    setAddEventOpen(true);
  };

  const handleAddEventClose = () => {
    setAddEventOpen(false);
    setAddEventCard(null);
  };
  // ── track which stage a card is being dragged from ───────────────
  const dragFromStageRef = useRef(null);

  const handleDragStart = (e, id) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";

    // Find which stage the card belongs to
    const stageKey = Object.entries(cards).find(([, stageCards]) =>
      (stageCards || []).some((c) => c._id === id),
    );
    dragFromStageRef.current = stageKey ? stageKey[0] : null;
  };

  // Resolve the stage id (e.g. "inTransit") back to the stage key used in VALID_TRANSITIONS ("in_transit")
  const resolveStageId = (stageKey) => {
    const found = STAGES.find((s) => s.key === stageKey);
    return found ? found.id : stageKey;
  };

  const handleDrop = (e, targetStageId) => {
    e.preventDefault();

    if (!dragId || !dragFromStageRef.current) {
      setDragId(null);
      setDragOver(null);
      return;
    }

    const fromStageId = resolveStageId(dragFromStageRef.current);

    // Block disabled stages
    if (DISABLED_DRAG_FROM.includes(fromStageId)) {
      setDragId(null);
      setDragOver(null);
      dragFromStageRef.current = null;
      return;
    }

    // Only allow the single valid next transition
    const validTarget = VALID_TRANSITIONS[fromStageId];
    if (!validTarget || validTarget !== targetStageId) {
      setDragId(null);
      setDragOver(null);
      dragFromStageRef.current = null;
      return;
    }

    // Find the actual card object
    const stageCards = cards[dragFromStageRef.current] || [];
    const card = stageCards.find((c) => c._id === dragId);
    if (!card) {
      setDragId(null);
      setDragOver(null);
      dragFromStageRef.current = null;
      return;
    }

    // Open the modal with the appropriate form
    setModalCard(card);
    setModalFromStage(fromStageId);
    setModalToStage(targetStageId);
    setModalOpen(true);

    setDragId(null);
    setDragOver(null);
    dragFromStageRef.current = null;
  };

  // Called when the modal form API call succeeds → move the card in state
  const handleModalSuccess = () => {
    setCards((prev) => {
      const fromKey = STAGES.find((s) => s.id === modalFromStage)?.key;
      const toKey = STAGES.find((s) => s.id === modalToStage)?.key;
      if (!fromKey || !toKey) return prev;

      const movedCard = (prev[fromKey] || []).find(
        (c) => c._id === modalCard._id,
      );
      if (!movedCard) return prev;

      const updatedCard = { ...movedCard, stage: modalToStage, daysInStage: 0 };

      // Record the pending move so stale re-fetches cannot flicker it back
      if (onCardMoved) {
        onCardMoved(modalCard._id, fromKey, toKey, updatedCard);
      }

      return {
        ...prev,
        [fromKey]: (prev[fromKey] || []).filter((c) => c._id !== modalCard._id),
        [toKey]: [...(prev[toKey] || []), updatedCard],
      };
    });
    setModalOpen(false);
    setModalCard(null);
    setModalFromStage(null);
    setModalToStage(null);
    // Re-fetch kanban data so the moved card gets the correct backend shape
    // (the optimistic card may have stale structure, e.g. backlog card moved to po_issued)
    if (onRefresh) onRefresh();
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setModalCard(null);
    setModalFromStage(null);
    setModalToStage(null);
  };

  return (
    <>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div
        style={{ position: "relative", flex: 1, display: "flex", minHeight: 0 }}
      >
        {/* Filter-change loading overlay – sits on top, doesn't shift columns */}
        {isFetching && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 20,
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "center",
              paddingTop: 20,
              pointerEvents: "none",
            }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                background: "rgba(18,18,28,0.82)",
                border: "1px solid rgba(76,175,80,0.25)",
                borderRadius: 8,
                padding: "6px 14px",
                backdropFilter: "blur(4px)",
              }}
            >
              <span
                style={{
                  width: 14,
                  height: 14,
                  border: "2px solid #444",
                  borderTopColor: "#4caf50",
                  borderRadius: "50%",
                  display: "inline-block",
                  animation: "spin 0.8s linear infinite",
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  fontSize: 12,
                  fontFamily: "Poppins",
                  color: "#aaa",
                }}
              >
                Applying filters…
              </span>
            </div>
          </div>
        )}
        <div
          ref={scrollRef}
          style={{
            flex: 1,
            overflowX: "auto",
            overflowY: "hidden",
            display: "flex",
            padding: "5px 5px 0",
            gap: 12,
            opacity: isFetching ? 0.5 : 1,
            pointerEvents: isFetching ? "none" : "auto",
            transition: "opacity 0.2s",
          }}
        >
          {VISIBLE_STAGES.map((stage) => (
            <KanbanColumn
              key={stage.id}
              stage={stage}
              stageCards={cards[stage.key]}
              allStageCards={cards[stage.key]}
              dragOver={dragOver}
              newCardIds={newCardIds}
              removingCardIds={removingCardIds}
              movingCardIds={movingCardIds}
              onDragOver={(id) => {
                // Show drop highlight only on valid target column
                const fromStageId = dragFromStageRef.current
                  ? resolveStageId(dragFromStageRef.current)
                  : null;
                const isValidTarget =
                  fromStageId &&
                  !DISABLED_DRAG_FROM.includes(fromStageId) &&
                  VALID_TRANSITIONS[fromStageId] === stage.id;
                if (isValidTarget) setDragOver(id);
              }}
              onDragLeave={() => setDragOver(null)}
              onDrop={handleDrop}
              onDragStart={handleDragStart}
              onAddEvent={handleAddEvent}
            />
          ))}
        </div>
      </div>

      <KanbanStageModal
        open={modalOpen}
        onClose={handleModalClose}
        card={modalCard}
        fromStage={modalFromStage}
        toStage={modalToStage}
        onSuccess={handleModalSuccess}
      />

      {/* Add Tracking Event modal — for in_transit cards (no stage move) */}
      <KanbanStageModal
        open={addEventOpen}
        onClose={handleAddEventClose}
        card={addEventCard}
        fromStage="in_transit"
        toStage="receiving"
        onSuccess={handleAddEventClose}
        addEventMode
      />
    </>
  );
};

export default BoardView;
